import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  updateDoc,
  getDoc,
  Timestamp,
  setDoc,
  deleteDoc,
  serverTimestamp,
  arrayUnion,
} from 'firebase/firestore'
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage'

import { db, storage } from '../../config/firebase'
import {
  BusinessDocument,
  CalendarEventDocument,
  RepeatingEventDocument,
  WorkspaceInvitationDocument,
  UserDocument,
  DocumentDocument,
  WorkspaceDocument,
  DocumentSignatories,
  DocumentSigningStatus,
  DocumentSigningStatusHistory,
  DocumentSigningTemplateBlock,
} from './db.types'

export const initialiseUser = async ({
  userId,
  email,
  name,
}: {
  userId: string
  email: string
  name?: string
}) => {
  console.log('INITIALISE USER', { userId, email, name })
  const userRef = doc(db, 'users', userId)
  const currentDoc = await getDoc(userRef)

  const data: Partial<UserDocument> = {
    email,
    createdAt: Timestamp.now(),
  }

  if (name) {
    data.name = name
  }

  if (currentDoc.exists()) {
    const data = currentDoc.data() as UserDocument
    if (data.email && data.createdAt) {
      return
    }

    console.log('UPDATE USER DOC', data)
    await updateDoc(userRef, data)
  } else {
    console.log('CREATE USER DOC', data)
    await setDoc(userRef, data)
  }
}

export const updateUserProfile = async ({
  userId,
  updates,
}: {
  userId: string
  updates: Partial<UserDocument>
}) => {
  const userRef = doc(db, 'users', userId)
  await updateDoc(userRef, updates)
}

export const subscribeToUserProfile = ({
  userId,
  onUpdate,
}: {
  userId: string
  onUpdate: (user: UserDocument) => void
}) => {
  const q = doc(db, 'users', userId)
  return onSnapshot(q, (doc) => {
    onUpdate(doc.data() as UserDocument)
  })
}

export const subscribeToWorkspaceCalendarEvents = ({
  workspaceId,
  startDate,
  endDate,
  onUpdate,
}: {
  workspaceId: string
  startDate: string
  endDate: string
  onUpdate: (events: Array<CalendarEventDocument & { id: string }>) => void
}) => {
  const q = query(
    collection(db, 'workspaces', workspaceId, 'calendarEvents'),
    where('date', '>=', startDate),
    where('date', '<=', endDate),
  )
  return onSnapshot(q, (querySnapshot) => {
    const events = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...(doc.data() as CalendarEventDocument),
    }))
    onUpdate(events)
  })
}

export const subscribeToWorkspaceRepeatingEvents = ({
  workspaceId,
  onUpdate,
}: {
  workspaceId: string
  onUpdate: (events: Array<RepeatingEventDocument & { id: string }>) => void
}) => {
  const q = query(collection(db, 'workspaces', workspaceId, 'repeatingEvents'))
  return onSnapshot(q, (querySnapshot) => {
    const events = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...(doc.data() as RepeatingEventDocument),
    }))
    onUpdate(events)
  })
}

export const subscribeToMyWorkspaces = ({
  userId,
  onUpdate,
}: {
  userId: string
  onUpdate: (workspaces: Array<WorkspaceDocument & { id: string }>) => void
}) => {
  const q = query(
    collection(db, 'workspaces'),
    where('users', 'array-contains', userId),
  )

  return onSnapshot(
    q,
    (querySnapshot) => {
      const workspaces = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...(doc.data() as WorkspaceDocument),
      }))
      onUpdate(workspaces)
    },
    (err) => {
      console.log('ERROR SUBSCRIBING TO MY WORKSPACES', err)
    },
  )
}

export const fetchBusinessDetails = async ({
  businessId,
}: {
  businessId: string
}) => {
  const q = doc(db, 'businesses', businessId)

  const businessDoc = await getDoc(q)
  return businessDoc.data() as BusinessDocument
}

export const updateCalendarEvent = async ({
  workspaceId,
  eventId,
  updates,
}: {
  workspaceId: string
  eventId: string
  updates: Partial<CalendarEventDocument>
}) => {
  const eventRef = doc(
    db,
    `workspaces/${workspaceId}/calendarEvents/${eventId}`,
  )
  await updateDoc(eventRef, updates)
}

export const createRecurringEvent = async ({
  workspaceId,
  event,
}: {
  workspaceId: string
  event: RepeatingEventDocument
}) => {
  const eventRef = doc(
    collection(db, `workspaces/${workspaceId}/repeatingEvents`),
  )
  await setDoc(eventRef, event)
}

export const updateRecurringEvent = async ({
  workspaceId,
  eventId,
  updates,
}: {
  workspaceId: string
  eventId: string
  updates: Partial<RepeatingEventDocument>
}) => {
  const eventRef = doc(
    db,
    `workspaces/${workspaceId}/repeatingEvents/${eventId}`,
  )
  await updateDoc(eventRef, updates)
}

export const deleteRecurringEvent = async ({
  workspaceId,
  eventId,
}: {
  workspaceId: string
  eventId: string
}) => {
  const eventRef = doc(
    db,
    `workspaces/${workspaceId}/repeatingEvents/${eventId}`,
  )
  await deleteDoc(eventRef)
}

export const subscribeToWorkspaceInvitations = ({
  workspaceId,
  onUpdate,
}: {
  workspaceId: string
  onUpdate: (
    invitations: Array<WorkspaceInvitationDocument & { id: string }>,
  ) => void
}) => {
  const q = query(collection(db, `workspaces/${workspaceId}/invitations`))

  return onSnapshot(q, (querySnapshot) => {
    const invitations = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...(doc.data() as WorkspaceInvitationDocument),
    }))
    onUpdate(invitations)
  })
}

export const updateBusinessDetails = async ({
  businessId,
  updates,
}: {
  businessId: string
  updates: Pick<Partial<BusinessDocument>, 'vatSettings'>
}) => {
  const businessRef = doc(db, 'businesses', businessId)
  await updateDoc(businessRef, updates)
}

export const uploadDocument = async ({
  file,
  workspaceId,
  userId,
  onProgress,
}: {
  file: File
  workspaceId: string
  userId: string
  onProgress?: (progress: number) => void
}): Promise<string> => {
  const documentsRef = collection(db, `workspaces/${workspaceId}/documents`)

  const newDocRef = doc(documentsRef)

  const storagePath = `documents/workspaces/${workspaceId}/${newDocRef.id}`

  const storageRef = ref(storage, storagePath)
  const uploadTask = uploadBytesResumable(storageRef, file)

  return new Promise<string>((resolve, reject) => {
    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
        onProgress?.(progress)
      },
      reject,
      async () => {
        const newDocument: DocumentDocument = {
          name: file.name,
          metadata: {
            fileName: file.name,
            mimeType: file.type,
            size: file.size,
            uploadedAt: serverTimestamp() as Timestamp,
            uploadedBy: userId,
            lastModifiedAt: serverTimestamp() as Timestamp,
            lastModifiedBy: userId,
          },
          storageRef: uploadTask.snapshot.ref.fullPath,
          workspaceId,
        }

        await setDoc(newDocRef, newDocument)
        resolve(newDocRef.id)
      },
    )
  })
}

export const updateDocumentDetails = async ({
  userId,
  documentId,
  workspaceId,
  details,
}: {
  userId: string
  documentId: string
  workspaceId: string
  details: Partial<DocumentDocument>
}) => {
  const documentRef = doc(
    db,
    `workspaces/${workspaceId}/documents/${documentId}`,
  )
  const update = {
    ...details,
    [`metadata.lastModifiedAt`]: serverTimestamp() as Timestamp,
    [`metadata.lastModifiedBy`]: userId,
  }
  await updateDoc(documentRef, update)
}

export const subscribeToDocuments = ({
  workspaceId,
  onUpdate,
}: {
  workspaceId: string
  onUpdate: (documents: Array<{ id: string; data: DocumentDocument }>) => void
}) => {
  const documentsRef = collection(db, `workspaces/${workspaceId}/documents`)

  const q = query(documentsRef)

  return onSnapshot(q, (snapshot) => {
    const docs = snapshot.docs.map((doc) => ({
      id: doc.id,
      data: doc.data() as DocumentDocument,
    }))
    onUpdate(docs.filter((d) => !!d.data.metadata.lastModifiedAt))
  })
}

export const getDocumentDownloadUrl = async (storageRef: string) => {
  const fileRef = ref(storage, storageRef)
  return await getDownloadURL(fileRef)
}

export const subscribeToDocument = ({
  workspaceId,
  documentId,
  onUpdate,
}: {
  workspaceId: string
  documentId: string
  onUpdate: (document: DocumentDocument) => void
}) => {
  const documentRef = doc(
    db,
    `workspaces/${workspaceId}/documents/${documentId}`,
  )

  return onSnapshot(documentRef, (doc) => {
    if (doc) {
      const data = doc.data() as DocumentDocument
      onUpdate(data)
    }
  })
}

export const deleteDocument = async ({
  workspaceId,
  documentId,
  storageRef,
}: {
  workspaceId: string
  documentId: string
  storageRef: string
}) => {
  // Delete from Storage - continue even if file doesn't exist
  const fileRef = ref(storage, storageRef)
  try {
    await deleteObject(fileRef)
  } catch (error) {
    // Ignore error if file doesn't exist in storage
    console.log('Storage file may not exist:', error)
  }

  // Delete from Firestore
  const documentRef = doc(
    db,
    `workspaces/${workspaceId}/documents/${documentId}`,
  )
  await deleteDoc(documentRef)
}

export const updateDocumentSignatories = async ({
  workspaceId,
  documentId,
  signatories,
}: {
  workspaceId: string
  documentId: string
  signatories: DocumentSignatories
}) => {
  const docRef = doc(db, `workspaces/${workspaceId}/documents/${documentId}`)
  await updateDoc(docRef, {
    'signing.signatories': signatories,
  })
}

export const updateDocumentSigningTemplateBlocks = async ({
  workspaceId,
  documentId,
  blocks,
}: {
  workspaceId: string
  documentId: string
  blocks: DocumentSigningTemplateBlock[]
}) => {
  const docRef = doc(db, `workspaces/${workspaceId}/documents/${documentId}`)
  await updateDoc(docRef, { 'signing.template.blocks': blocks })
}

export const updateDocumentSigningTemplatePageDimensions = async ({
  workspaceId,
  documentId,
  pageDimensions,
}: {
  workspaceId: string
  documentId: string
  pageDimensions: Array<{ width: number; height: number }>
}) => {
  const docRef = doc(db, `workspaces/${workspaceId}/documents/${documentId}`)
  await updateDoc(docRef, { 'signing.template.pageDimensions': pageDimensions })
}

export const updateDocumentSigningStatus = async ({
  workspaceId,
  documentId,
  status,
  userId,
}: {
  workspaceId: string
  documentId: string
  status: DocumentSigningStatus
  userId: string
}) => {
  const docRef = doc(db, `workspaces/${workspaceId}/documents/${documentId}`)

  const documentData = await getDoc(docRef)
  const document = documentData.data() as DocumentDocument

  const statusHistory: DocumentSigningStatusHistory = {
    status,
    timestamp: Timestamp.now(),
    actorId: userId,
  }

  const delta: Record<string, any> = {
    'signing.status': status,
    'signing.statusHistory': arrayUnion(statusHistory),
  }

  if (status === 'signing-in-progress' && !document.signing?.currentSignerId) {
    const signatories = Object.values(document.signing?.signatories || {})
      .filter((s) => s.status === 'pending')
      .sort((a, b) => a.order - b.order)

    delta['signing.currentSignerId'] = signatories[0].id
  }

  await updateDoc(docRef, delta)
}
